import type { SelectOption } from '@aus-platform/design-system';
import {
  Button,
  ButtonVariant,
  Input,
  InputGroup,
  Placement,
  toast,
} from '@aus-platform/design-system';
import { useAppSelector } from 'app/hooks';
import { isEmpty, isNil, snakeCase } from 'lodash';
import { LineEventType, PolygonEventType } from 'map-3d/shared';
import {
  selectActiveWorkspaceLayersByFeatureType,
  selectMap3dDataset,
  selectMap3DState,
  selectMap3dWorkspace,
  setCurrentActiveMapTool,
} from 'map-3d/shared/map-3d-slices';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  FeatureCountLabel,
  FeatureGeometryType,
  FeatureType,
  handleResponseErrorMessage,
  useCreateDrillBlastRequest,
  useFeatureList,
  type FeatureResponseData,
} from 'shared/api';
import { useDrillBlastRequestList } from 'shared/api/workspace';
import { Placeholders } from 'shared/constants';
import {
  allowNegativeNumberKeyDown,
  clampDecimalInput,
  preventNonInteger,
  preventNonNumber,
} from 'shared/helpers';
import { useInputFields } from 'shared/hooks';
import { MapTool } from '../../../../map-3d-container/map-3d-tools';
import type { WorkspaceLayer } from '../../../shared';
import {
  MarkArea,
  MarkAreaDrawingToolType,
  MarkAreaToggle,
} from '../../../shared';
import { FormErrorMessage } from '../../../shared/enums';
import { setVectorLayerVisibility } from '../../map-3d-workspace';
import { getEligibleLayerOptions } from '../../map-3d-workspace/shared/helpers';
import {
  drillAndBlastInitialState,
  drillPatternOptions,
  explosiveTypeOptions,
} from './constants';
import { TooltipInfoCopy } from './enums';
import {
  areRequiredFieldsValid,
  extractUniqueIdsFromDrawnItems,
  extractWktFromExportedItems,
  extractWktFromFeatures,
  normalizeExpectedNumberOfHoles,
} from './helpers';
import type { DrillAndBlastFormType } from './types';
import { drillAndBlastValidator } from './validator';

export const DrillAndBlastGenerateForm: React.FC = () => {
  // Selectors.
  const { selectedTerrainIteration, selectedTerrainSite } =
    useAppSelector(selectMap3dDataset);
  const { workspaceLayers } = useAppSelector(selectMap3dWorkspace);
  const { aoiManager } = useAppSelector(selectMap3DState);
  const dispatch = useDispatch();

  // States.
  const [aoiMarkedArea, setAoiMarkedArea] = useState<MarkArea>(MarkArea.Draw);
  const [extentMarkedArea, setExtentMarkedArea] = useState<MarkArea>(
    MarkArea.Draw,
  );
  const [aoiDrawingActive, setAoiDrawingActive] = useState<boolean>(false);
  const [extentDrawingActive, setExtentDrawingActive] =
    useState<boolean>(false);
  const [aoiSelected, setAoiSelected] = useState<boolean>(false);

  const {
    values,
    setValues,
    names,
    errors,
    setErrors,
    onChange,
    onBlur,
    onFocus,
    resetAll,
  } = useInputFields<DrillAndBlastFormType>(
    drillAndBlastInitialState,
    drillAndBlastValidator,
  );

  // Options - Layer from workspace (exactly one feature).
  const activePolygonLayers = useAppSelector((state) =>
    selectActiveWorkspaceLayersByFeatureType(
      state,
      FeatureGeometryType.Polygon,
    ),
  );

  const activeLineLayers = useAppSelector((state) =>
    selectActiveWorkspaceLayersByFeatureType(state, FeatureGeometryType.Line),
  );

  const polygonLayerOptions: SelectOption<WorkspaceLayer>[] = useMemo(() => {
    return getEligibleLayerOptions(
      activePolygonLayers,
      FeatureCountLabel.Polygons,
      1,
    );
  }, [activePolygonLayers]);

  const lineLayerOptions: SelectOption<WorkspaceLayer>[] = useMemo(() => {
    return getEligibleLayerOptions(
      activeLineLayers,
      FeatureCountLabel.Lines,
      1,
    );
  }, [activeLineLayers]);

  const noEligiblePolygonLayers = isEmpty(polygonLayerOptions);
  const noEligibleLineLayers = isEmpty(lineLayerOptions);

  // APIs.
  const {
    data: aoiFeaturesResponse,
    isSuccess: isSuccessAoiFeatures,
    isError: isErrorAoiFeatures,
    error: aoiFeaturesError,
    refetch: refetchAoiFeatures,
  } = useFeatureList(
    values.aoiWorkspaceLayer?.value.id ?? null,
    FeatureType.Polygon,
    false,
  );

  const {
    data: extentFeaturesResponse,
    isSuccess: isSuccessExtentFeatures,
    isError: isErrorExtentFeatures,
    error: extentFeaturesError,
    refetch: refetchExtentFeatures,
  } = useFeatureList(
    values.extentWorkspaceLayer?.value.id ?? null,
    FeatureType.LineString,
    false,
  );

  const { mutate: createRequest, isPending: isCreating } =
    useCreateDrillBlastRequest();

  const { refetch: refetchHistory } = useDrillBlastRequestList(
    selectedTerrainIteration?.id ?? null,
    false,
  );

  // Drawing tools.
  const polygonTool = aoiManager?.polygonTool;
  const lineTool = aoiManager?.lineTool;

  const aoiPolygonIdRef = useRef<string | null>(null);
  const extentLineIdRef = useRef<string | null>(null);

  // useCallbacks.
  const updateDrawnAoi = useCallback(() => {
    const polygons =
      polygonTool?.getPolygonByProperty('drillBlastAoi', MarkArea.Draw) ?? [];
    const { count, lastId } = extractUniqueIdsFromDrawnItems(polygons);
    aoiPolygonIdRef.current = lastId;
    setAoiSelected(count === 1);
  }, [polygonTool]);

  const updateDrawnExtent = useCallback(() => {
    const lines =
      lineTool?.getLinesByProperty('drillBlastExtent', MarkArea.Draw) ?? [];
    const { lastId } = extractUniqueIdsFromDrawnItems(lines);
    extentLineIdRef.current = lastId;
  }, [lineTool]);

  // Import features from workspace on selection.
  const importAoiPolygonsFromWorkspace = (features: FeatureResponseData[]) => {
    const polygon = features.find(
      (feature) => feature.type === FeatureType.Polygon,
    );

    if (!isNil(polygon)) {
      polygonTool?.importWKT(
        polygon.geometry,
        polygon.id,
        { drillBlastAoi: MarkArea.LayerFromWorkspace },
        polygon.name,
      );
      aoiPolygonIdRef.current = polygon.id;
    }

    setAoiSelected(true);
  };

  const importExtentLineFromWorkspace = (features: FeatureResponseData[]) => {
    const line = features.find(
      (feature) => feature.type === FeatureType.LineString,
    );

    if (!isNil(line)) {
      lineTool?.importWKT(
        line.geometry,
        line.id,
        { drillBlastExtent: MarkArea.LayerFromWorkspace },
        line.name,
      );
      extentLineIdRef.current = line.id;
    }
  };

  // useEffects.
  useEffect(() => {
    return () => {
      setVectorLayerVisibility(workspaceLayers, true);
    };
  }, []);

  useEffect(() => {
    if (!polygonTool || !aoiDrawingActive) {
      return;
    }

    polygonTool.addEventListener(
      PolygonEventType.PolygonCreated,
      updateDrawnAoi,
    );

    return () => {
      polygonTool.removeEventListener(
        PolygonEventType.PolygonCreated,
        updateDrawnAoi,
      );
    };
  }, [polygonTool, aoiDrawingActive, updateDrawnAoi]);

  useEffect(() => {
    if (!lineTool || !extentDrawingActive) {
      return;
    }

    lineTool.addEventListener(LineEventType.LineCreated, updateDrawnExtent);

    return () => {
      lineTool.removeEventListener(
        LineEventType.LineCreated,
        updateDrawnExtent,
      );
    };
  }, [lineTool, extentDrawingActive, updateDrawnExtent]);

  useEffect(() => {
    handleResponseErrorMessage(isErrorAoiFeatures, aoiFeaturesError);
    handleResponseErrorMessage(isErrorExtentFeatures, extentFeaturesError);
  }, [
    isErrorAoiFeatures,
    aoiFeaturesError,
    isErrorExtentFeatures,
    extentFeaturesError,
  ]);

  useEffect(() => {
    if (!isNil(values.aoiWorkspaceLayer)) {
      refetchAoiFeatures();
      // Clear drawn AOI before importing from workspace.
      polygonTool?.deleteAllPolygons();
    }
  }, [values.aoiWorkspaceLayer]);

  useEffect(() => {
    if (isSuccessAoiFeatures && aoiFeaturesResponse?.data?.features) {
      importAoiPolygonsFromWorkspace(aoiFeaturesResponse.data.features);
    }
  }, [isSuccessAoiFeatures, aoiFeaturesResponse]);

  useEffect(() => {
    if (!isNil(values.extentWorkspaceLayer)) {
      refetchExtentFeatures();
      lineTool?.deleteAllLines();
    }
  }, [values.extentWorkspaceLayer]);

  useEffect(() => {
    if (isSuccessExtentFeatures && extentFeaturesResponse?.data?.features) {
      importExtentLineFromWorkspace(extentFeaturesResponse.data.features);
    }
  }, [isSuccessExtentFeatures, extentFeaturesResponse]);

  // Drawing handlers wired to tools.
  const aoiStartDrawingHandler = () => {
    // Clear any previously imported/drawn AOI polygons before starting a new draw.
    const imported =
      polygonTool?.getPolygonByProperty?.(
        'drillBlastAoi',
        MarkArea.LayerFromWorkspace,
      ) ?? [];

    if (Array.isArray(imported)) {
      for (const polygon of imported) {
        if (polygon?.id) {
          polygonTool?.deleteById?.(polygon.id);
        }
      }
    }

    const drawnExisting =
      polygonTool?.getPolygonByProperty?.('drillBlastAoi', MarkArea.Draw) ?? [];

    if (Array.isArray(drawnExisting)) {
      for (const polygon of drawnExisting) {
        if (polygon?.id) {
          polygonTool?.deleteById?.(polygon.id);
        }
      }
    }

    polygonTool?.activate({ drillBlastAoi: MarkArea.Draw });
    dispatch(setCurrentActiveMapTool(MapTool.None));
    setVectorLayerVisibility(workspaceLayers, false);
    setAoiDrawingActive(true);
    setAoiSelected(false);
    aoiPolygonIdRef.current = null;
  };

  const aoiEndDrawingHandler = () => {
    polygonTool?.deactivate();
    setAoiDrawingActive(false);
    updateDrawnAoi();
  };

  const aoiResetDrawingHandler = () => {
    polygonTool?.deleteAllPolygons();
    setAoiSelected(false);
    aoiPolygonIdRef.current = null;
  };

  const extentStartDrawingHandler = () => {
    lineTool?.activate({ drillBlastExtent: MarkArea.Draw }, {}, true);
    dispatch(setCurrentActiveMapTool(MapTool.None));
    setVectorLayerVisibility(workspaceLayers, false);
    setExtentDrawingActive(true);
    extentLineIdRef.current = null;
  };

  const extentEndDrawingHandler = () => {
    lineTool?.deactivate();
    setExtentDrawingActive(false);
    updateDrawnExtent();
  };

  const extentResetDrawingHandler = () => {
    if (lineTool?.isAnyLineDrawn) {
      lineTool?.deleteAllLines();
      lineTool?.deactivate();
      lineTool?.activate({ drillBlastExtent: MarkArea.Draw }, {}, true);
    }

    extentLineIdRef.current = null;
  };

  // Helper functions for resetting drawing state.
  const resetAoiDrawingState = () => {
    polygonTool?.deleteAllPolygons();
    polygonTool?.deactivate();
    setAoiDrawingActive(false);
    aoiPolygonIdRef.current = null;
  };

  const resetExtentDrawingState = () => {
    lineTool?.deleteAllLines();
    lineTool?.deactivate();
    setExtentDrawingActive(false);
    extentLineIdRef.current = null;
  };

  // Reset form to initial state.
  const resetForm = () => {
    resetAll();

    resetAoiDrawingState();
    resetExtentDrawingState();
    setAoiSelected(false);

    setAoiMarkedArea(MarkArea.Draw);
    setExtentMarkedArea(MarkArea.Draw);

    dispatch(setCurrentActiveMapTool(MapTool.None));

    setVectorLayerVisibility(workspaceLayers, true);
  };

  // Enablement logic.
  const drawnPolygons =
    polygonTool?.getPolygonByProperty?.('drillBlastAoi', MarkArea.Draw) ?? [];

  const isGenerateDisabled = !areRequiredFieldsValid(
    values,
    drawnPolygons,
    aoiMarkedArea,
  );

  // Toggle handlers extracted for readability.
  const handleAoiToggleChange = (toggledLabel: string) => {
    const nextMarkedArea = snakeCase(toggledLabel) as MarkArea;
    setAoiMarkedArea(nextMarkedArea);
    setAoiSelected(
      nextMarkedArea === MarkArea.Draw
        ? aoiSelected
        : !isNil(values.aoiWorkspaceLayer),
    );

    // Clear previously selected layer when switching to Draw.
    if (nextMarkedArea === MarkArea.Draw && !isNil(values.aoiWorkspaceLayer)) {
      setValues({ ...values, aoiWorkspaceLayer: null });
      // Clear AOI workspace error
      if (errors.aoiWorkspaceLayer) {
        setErrors({ ...errors, aoiWorkspaceLayer: '' });
      }
      // Remove previously imported workspace polygons and hide workspace layers.
      const imported =
        polygonTool?.getPolygonByProperty?.(
          'drillBlastAoi',
          MarkArea.LayerFromWorkspace,
        ) ?? [];
      if (Array.isArray(imported)) {
        for (const poly of imported) {
          if (poly?.id) {
            polygonTool?.deleteById?.(poly.id);
          }
        }
      }
      setVectorLayerVisibility(workspaceLayers, false);
      setAoiSelected(false);
      // Ensure draw state is clean.
      polygonTool?.deactivate();
      setAoiDrawingActive(false);
      aoiPolygonIdRef.current = null;
    }
    // If switched to LayerFromWorkspace with no eligible layers, set error.
    if (
      nextMarkedArea === MarkArea.LayerFromWorkspace &&
      noEligiblePolygonLayers
    ) {
      setErrors({
        ...errors,
        aoiWorkspaceLayer: FormErrorMessage.NoActivePolygonLayer,
      });
      resetAoiDrawingState();
    } else if (nextMarkedArea === MarkArea.LayerFromWorkspace) {
      setErrors({ ...errors, aoiWorkspaceLayer: '' });
      resetAoiDrawingState();
    }
  };

  const handleExtentToggleChange = (toggledLabel: string) => {
    const nextMarkedArea = snakeCase(toggledLabel) as MarkArea;

    setExtentMarkedArea(nextMarkedArea);

    // Clear previously selected layer when switching to Draw.
    if (
      nextMarkedArea === MarkArea.Draw &&
      !isNil(values.extentWorkspaceLayer)
    ) {
      setValues({ ...values, extentWorkspaceLayer: null });
      if (errors.extentWorkspaceLayer) {
        setErrors({ ...errors, extentWorkspaceLayer: '' });
      }
      // Remove previously imported workspace lines and hide workspace layers
      const imported =
        lineTool?.getLinesByProperty?.(
          'drillBlastExtent',
          MarkArea.LayerFromWorkspace,
        ) ?? [];

      if (Array.isArray(imported)) {
        for (const line of imported) {
          if (line?.id) {
            lineTool?.deleteById?.(line.id);
          }
        }
      } else {
        lineTool?.deleteAllLines();
      }

      setVectorLayerVisibility(workspaceLayers, false);
      lineTool?.deactivate();
      setExtentDrawingActive(false);
      extentLineIdRef.current = null;
    }

    if (
      nextMarkedArea === MarkArea.LayerFromWorkspace &&
      noEligibleLineLayers
    ) {
      setErrors({
        ...errors,
        extentWorkspaceLayer: FormErrorMessage.NoActiveLineLayer,
      });
      resetExtentDrawingState();
    } else if (nextMarkedArea === MarkArea.LayerFromWorkspace) {
      setErrors({ ...errors, extentWorkspaceLayer: '' });
      resetExtentDrawingState();
    }
  };

  // Submit handler (payload assembly).
  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (isGenerateDisabled) {
      return;
    }

    // AOI WKT.
    let inputAoiWkt = '';

    if (aoiMarkedArea === MarkArea.Draw) {
      const exported = polygonTool?.exportCurrentPolygonsToWkt() ?? [];
      inputAoiWkt =
        extractWktFromExportedItems(exported, aoiPolygonIdRef.current, '') ??
        '';
    } else if (aoiFeaturesResponse?.data?.features?.length) {
      inputAoiWkt =
        extractWktFromFeatures(
          aoiFeaturesResponse.data.features,
          aoiPolygonIdRef.current,
          '',
        ) ?? '';
    }

    // Extent WKT (optional).
    let extentWkt: string | undefined = undefined;

    if (extentMarkedArea === MarkArea.Draw) {
      const exported = lineTool?.exportCurrentLinesToWKT() ?? [];
      extentWkt = extractWktFromExportedItems(
        exported,
        extentLineIdRef.current,
        undefined,
      );
    } else if (extentFeaturesResponse?.data?.features?.length) {
      extentWkt = extractWktFromFeatures(
        extentFeaturesResponse.data.features,
        extentLineIdRef.current,
        undefined,
      );
    }

    // expected_number_of_holes as number (optional)
    const expectedNumberOfHoles = normalizeExpectedNumberOfHoles(
      values.expectedHoles,
    );

    const payload = {
      title: values.title,
      inputAoiGeom: inputAoiWkt,
      iterationId: selectedTerrainIteration?.id ?? '',
      blastExtentGeom: extentWkt,
      spacing: String(values.spacingMeters ?? ''),
      burden: String(values.burdenMeters ?? ''),
      baseElevation: String(values.baseElevationMeters ?? ''),
      expectedNumberOfHoles: expectedNumberOfHoles,
      drillPattern: values.drillPattern?.value ?? '',
      explosiveType: values.explosiveType?.value ?? '',
    };

    createRequest(payload, {
      onSuccess: () => {
        toast.success('Request generated successfully.');
        refetchHistory();
        resetForm();
      },
      onError: () => {
        toast.error('Failed to generate request.');
        refetchHistory();
      },
    });
  };

  // Components.
  const MarkAreaHeader = () => (
    <Input.Label
      info={TooltipInfoCopy.ExtentTooltip}
      infoPlacement={Placement.Top}
      tooltipClassname="drill-and-blast-mark-area-tooltip"
    >
      Extent for the Blast
    </Input.Label>
  );

  return (
    <form className="drill-and-blast__container" onSubmit={onSubmit}>
      <div className="input-field-container map-3d-sidebar-content">
        {/* Active Dataset */}
        <InputGroup className="input-field--bold">
          <Input.Label>Active Dataset</Input.Label>
        </InputGroup>
        <InputGroup>
          <Input.Label>Site</Input.Label>
          <Input.Text disabled value={selectedTerrainSite?.name ?? ''} />
        </InputGroup>
        <InputGroup>
          <Input.Label>Iteration</Input.Label>
          <Input.Text disabled value={selectedTerrainIteration?.name ?? ''} />
        </InputGroup>

        <InputGroup className="input-field--bold">
          <Input.Label isRequired>Title</Input.Label>
          <Input.Text
            name={names.title}
            value={values.title}
            error={errors.title}
            isInvalid={!!errors.title}
            {...{ onChange, onBlur, onFocus }}
            placeholder="Title"
            disabled={isCreating}
          />
        </InputGroup>
        {/* AOI - Polygon */}
        <MarkAreaToggle
          currentMarkedArea={aoiMarkedArea}
          onToggleChange={handleAoiToggleChange}
          drawingHandlers={{
            startDrawingHandler: aoiStartDrawingHandler,
            endDrawingHandler: aoiEndDrawingHandler,
            resetDrawingHandler: aoiResetDrawingHandler,
          }}
          isDrawingToolEnabled={aoiDrawingActive}
          workspaceLayerError={errors.aoiWorkspaceLayer}
          selectLayerSelectProps={{
            options: polygonLayerOptions,
            value: values.aoiWorkspaceLayer,
            placeholder: Placeholders.SelectLayerFromWorkspace,
            isDisabled: isEmpty(polygonLayerOptions),
            onChange: (opt: SelectOption<WorkspaceLayer>) => {
              setValues({ ...values, aoiWorkspaceLayer: opt });
              setAoiSelected(!!opt);
            },
          }}
          drawingType={MarkAreaDrawingToolType.Polygon}
          isDisabled={extentDrawingActive || isCreating}
          headerComponent={
            <Input.Label isRequired className="mark-area__heading">
              Mark Area of Interest
            </Input.Label>
          }
          buttonsTooltipProps={{
            text: TooltipInfoCopy.AoiDisabledByExtent,
            className: 'drill-and-blast-mark-area-tooltip',
            showWhenDisabled: !isCreating,
          }}
        />

        {/* Extent for the Blast - Line (optional) */}
        <MarkAreaToggle
          currentMarkedArea={extentMarkedArea}
          onToggleChange={handleExtentToggleChange}
          drawingHandlers={{
            startDrawingHandler: extentStartDrawingHandler,
            endDrawingHandler: extentEndDrawingHandler,
            resetDrawingHandler: extentResetDrawingHandler,
          }}
          isDrawingToolEnabled={extentDrawingActive}
          workspaceLayerError={errors.extentWorkspaceLayer}
          selectLayerSelectProps={{
            options: lineLayerOptions,
            value: values.extentWorkspaceLayer,
            placeholder: Placeholders.SelectLayerFromWorkspace,
            isDisabled: isEmpty(lineLayerOptions),
            onChange: (opt: SelectOption<WorkspaceLayer>) => {
              setValues({ ...values, extentWorkspaceLayer: opt });
            },
          }}
          drawingType={MarkAreaDrawingToolType.Line}
          isDisabled={aoiDrawingActive || isCreating}
          headerComponent={<MarkAreaHeader />}
          buttonsTooltipProps={{
            text: TooltipInfoCopy.ExtentDisabledByAoi,
            className: 'drill-and-blast-mark-area-tooltip',
            showWhenDisabled: !isCreating,
          }}
        />

        {/* Parameters */}
        <div className="parameters-row">
          <InputGroup className="input-field--bold">
            <Input.Label
              isRequired
              info={TooltipInfoCopy.Spacing}
              infoPlacement={Placement.Top}
              tooltipClassname="drill-and-blast-input-tooltip"
            >
              Spacing <span className="label-unit">(m)</span>
            </Input.Label>
            <Input.Number
              name={names.spacingMeters}
              value={values.spacingMeters ?? ''}
              min={0}
              step="any"
              onKeyDown={preventNonNumber}
              isInvalid={!!errors.spacingMeters}
              error={errors.spacingMeters}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setValues({
                  ...values,
                  spacingMeters: clampDecimalInput(
                    event.target.value ?? '',
                    2,
                    false,
                  ),
                })
              }
              disabled={isCreating}
              {...{ onBlur, onFocus }}
            />
          </InputGroup>

          <InputGroup className="input-field--bold">
            <Input.Label
              isRequired
              info={TooltipInfoCopy.Burden}
              infoPlacement={Placement.Top}
              tooltipClassname="drill-and-blast-input-tooltip"
            >
              Burden <span className="label-unit">(m)</span>
            </Input.Label>
            <Input.Number
              name={names.burdenMeters}
              value={values.burdenMeters ?? ''}
              min={0}
              step="any"
              onKeyDown={preventNonNumber}
              isInvalid={!!errors.burdenMeters}
              error={errors.burdenMeters}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setValues({
                  ...values,
                  burdenMeters: clampDecimalInput(
                    event.target.value ?? '',
                    2,
                    false,
                  ),
                })
              }
              disabled={isCreating}
              {...{ onBlur, onFocus }}
            />
          </InputGroup>

          <InputGroup className="input-field--bold">
            <Input.Label
              isRequired
              info={TooltipInfoCopy.BaseElevation}
              infoPlacement={Placement.Top}
              tooltipClassname="drill-and-blast-input-tooltip"
            >
              Base Elevation <span className="label-unit">(m)</span>
            </Input.Label>
            <Input.Number
              name={names.baseElevationMeters}
              value={values.baseElevationMeters ?? ''}
              step="any"
              onKeyDown={allowNegativeNumberKeyDown}
              isInvalid={!!errors.baseElevationMeters}
              error={errors.baseElevationMeters}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setValues({
                  ...values,
                  baseElevationMeters: clampDecimalInput(
                    event.target.value ?? '',
                    3,
                    true,
                  ),
                })
              }
              disabled={isCreating}
              {...{ onBlur, onFocus }}
            />
          </InputGroup>

          <InputGroup className="input-field--bold">
            <Input.Label
              info={TooltipInfoCopy.ExpectedNumberOfHoles}
              infoPlacement={Placement.Top}
              tooltipClassname="drill-and-blast-input-tooltip"
            >
              Expected Number of Holes
            </Input.Label>
            <Input.Number
              name={names.expectedHoles}
              value={values.expectedHoles ?? ''}
              min={1}
              onKeyDown={preventNonInteger}
              isInvalid={!!errors.expectedHoles}
              error={errors.expectedHoles}
              disabled={isCreating}
              {...{ onChange, onBlur, onFocus }}
            />
          </InputGroup>
        </div>
        <InputGroup className="input-field--bold">
          <Input.Label isRequired>Drill Pattern</Input.Label>
          <Input.Select
            options={drillPatternOptions}
            value={values.drillPattern}
            onChange={(opt) => setValues({ ...values, drillPattern: opt })}
            isDisabled={isCreating}
          />
        </InputGroup>

        <InputGroup className="input-field--bold">
          <Input.Label isRequired>Explosive Type</Input.Label>
          <Input.Select
            options={explosiveTypeOptions}
            value={values.explosiveType}
            onChange={(opt) => setValues({ ...values, explosiveType: opt })}
            isDisabled={isCreating}
            menuPlacement={Placement.Top}
          />
        </InputGroup>
      </div>
      <div className="submit-btn">
        <Button
          disabled={isGenerateDisabled || isCreating}
          variant={ButtonVariant.Primary}
          type="submit"
          isLoading={isCreating}
        >
          {isCreating ? 'Generating Request' : 'Generate Request'}
        </Button>
      </div>
    </form>
  );
};
